import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import UniversalNavbar from './UniversalNavbar';
import VFooter from './VFooter';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';
import API_BASE_URL from '../config/api';
import Card from './ui/Card'; 

import { devLog } from '../utils/devLog';
function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSuccess(false);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/forgot_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setIsSuccess(true);
        setEmail('');
      } else {
        setError(data.error || 'Failed to send reset link. Please try again.');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please check your internet connection and try again.');
      devLog.error('Forgot password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-light via-white to-primary-light/50 font-fell">
      <UniversalNavbar />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Header */}
            <motion.div variants={itemVariants} className="text-center mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary font-semibold text-sm mb-4">
                <Mail size={16} className="mr-2" />
                Password Recovery
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-dark mb-2">
                Forgot Your Password?
              </h1>
              <p className="text-muted text-base">
                No worries! Enter your email and we'll send you reset instructions.
              </p>
            </motion.div>

            {/* Success State */}
            {isSuccess ? (
              <motion.div variants={itemVariants}>
                <Card className="text-center p-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-dark mb-3">Check Your Email!</h3>
                  <p className="text-muted mb-6">
                    If an account exists with that email, we've sent password reset instructions.
                    Please check your inbox and spam folder.
                  </p>
                  <Link to="/login">
                    <Button variant="primary" icon={<ArrowLeft size={18} />} fullWidth>
                      Back to Login
                    </Button>
                  </Link>
                </Card>
              </motion.div>
            ) : (
              /* Reset Form */
              <motion.div variants={itemVariants}>
                <Card className="p-8">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6"
                    >
                      <Alert variant="error" icon>
                        {error}
                      </Alert>
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <Input
                      type="email"
                      name="email"
                      id="email"
                      label="Email Address"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      icon={<Mail size={18} />}
                      required
                      helpText="Enter the email address associated with your account"
                    />

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={isLoading}
                    >
                      {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center text-sm text-primary hover:text-primary-dark font-medium transition-colors"
                    >
                      <ArrowLeft size={16} className="mr-2" />
                      Back to Login
                    </Link>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Help Text */}
            <motion.div variants={itemVariants} className="mt-8 text-center">
              <p className="text-sm text-muted mb-2">Need more help?</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center text-sm">
                <a
                  href="mailto:team@vasstr.com"
                  className="text-primary hover:text-primary-dark font-medium transition-colors"
                >
                  team@vasstr.com
                </a>
                <span className="hidden sm:inline text-gray-400">•</span>
                <span className="text-gray-600">+91 97043 87878</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <VFooter />
    </div>
  );
}

export default ForgotPassword;